import { createLogger } from '@ctrl-enter/tools/utils';
import * as React from 'react';

const logger = createLogger('web:component:workos-widget-error-boundary');

type WidgetErrorBoundaryProps = {
  children: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback?: React.ReactNode;
  onRetry?: () => void;
};

type WidgetErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
  lastErrorTime: number | null;
};

/**
 * Error boundary specifically for WorkOS widgets
 *
 * Catches and handles errors from widgets (including 403s) with:
 * - Automatic retry with exponential backoff
 * - User-friendly error messages
 * - Manual retry option
 * - Error logging for observability
 *
 * @example
 * ```tsx
 * <WorkOsWidgetErrorBoundary onRetry={() => refreshToken()}>
 * <UserProfile authToken={token} />
 * </WorkOsWidgetErrorBoundary>
 * ```
 */
export class WorkOsWidgetErrorBoundary extends React.Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorCount: 0,
      lastErrorTime: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<WidgetErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const now = Date.now();
    const timeSinceLastError = this.state.lastErrorTime ? now - this.state.lastErrorTime : Infinity;

    // Reset error count if it's been more than 5 minutes since last error
    const errorCount = timeSinceLastError > 5 * 60 * 1000 ? 1 : this.state.errorCount + 1;

    this.setState({
      errorCount,
      lastErrorTime: now,
    });

    // Log error for observability
    logger.error('🚨 [WORKOS-WIDGET-ERROR]', {
      error: error.message,
      errorCount,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date(now).toISOString(),
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Attempt automatic retry with exponential backoff (max 3 attempts)
    if (errorCount <= 3 && this.props.onRetry) {
      const backoffDelay = Math.min(1000 * Math.pow(2, errorCount - 1), 10000); // Max 10 seconds

      logger.info(`⏰ [WORKOS-WIDGET-ERROR] Scheduling retry #${errorCount} in ${backoffDelay}ms`);

      this.retryTimeoutId = setTimeout(() => {
        logger.info(`🔄 [WORKOS-WIDGET-ERROR] Attempting automatic retry #${errorCount}`);
        this.handleRetry();
      }, backoffDelay);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    logger.info('🔄 [WORKOS-WIDGET-ERROR] User initiated retry');

    // Reset error state
    this.setState({
      hasError: false,
      error: null,
    });

    // Call retry callback
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Check if it's a 403 error
      const is403Error =
        this.state.error.message.includes('403') || this.state.error.message.includes('Forbidden');

      const errorMessage = is403Error
        ? 'Your session expired. Please refresh to continue.'
        : `An error occurred: ${this.state.error.message}`;

      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex flex-col items-center justify-center p-8 space-y-4 border border-destructive/20 rounded-lg bg-destructive/5">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-destructive"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm font-medium text-destructive">Widget Error</div>
          </div>

          <div className="text-sm text-muted-foreground text-center max-w-md">{errorMessage}</div>

          {this.state.errorCount > 3 && (
            <div className="text-xs text-destructive/70 text-center">
              Multiple errors detected. Please check your connection or contact support.
            </div>
          )}

          <button
            onClick={this.handleRetry}
            className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-md hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-destructive/50"
          >
            Try Again
          </button>

          {process.env.NODE_ENV === 'development' && (
            <details className="text-xs text-muted-foreground max-w-md">
              <summary className="cursor-pointer hover:text-foreground">
                Error Details (Development)
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-40 text-xs">
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based wrapper for the error boundary (for functional component patterns)
 */
export const useWorkOsWidgetErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);

  const handleError = React.useCallback((err: Error) => {
    logger.error('🚨 [WORKOS-WIDGET] Error caught:', err);
    setError(err);
    setRetryCount(prev => prev + 1);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    retryCount,
    handleError,
    clearError,
  };
};
